import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { AuthenticationService } from '../../authentication/authentication.service';
import { PermissionService } from '../permission/permission.service';
import { UsersService } from '../../user-management/users/users.service';
import { SessionService } from '../session/session.service';
import { ClosingReportService } from '../../reports/closing-report/closing-report.service';

import * as fromStore from '../../account/store';
import { PermissionLevel } from '../permission/permission';
import { EstablishmentService } from '../establishment/establishment.service';
import { CompanyData } from '../../settings/model/company-data.model';
import { ModulesService } from '../modules/modules.service';
import { moduleId } from '../../device-management/model/module-id';
import { CfdService } from '../../device-management/cfd/cfd.service';
import { CompanySettingsServiceHelper } from '../../settings/services/company-settings-helper.service';
import { TokenService } from '../../authentication/token.service';

@Injectable()
export class AccessControlService {
  constructor(
    private authenticationService: AuthenticationService,
    private tokenService: TokenService,
    private permission: PermissionService,
    private usersService: UsersService,
    private session: SessionService,
    private closingReportService: ClosingReportService,
    private companySettingsServiceHelper: CompanySettingsServiceHelper,
    private establishment: EstablishmentService,
    private modulesService: ModulesService,
    private cfdService: CfdService,
    private store: Store<fromStore.AccountState>
  ) {}

  public isLoggedIn(): Observable<boolean> {
    return of(this.tokenService.hasToken());
  }

  public hasManagerPermission(): Observable<boolean> {
    return of(this.permission.hasAccess(PermissionLevel.MANAGER));
  }

  public hasEmployeePermission(): Observable<boolean> {
    return of(this.permission.hasAccess(PermissionLevel.EMPLOYEE));
  }

  public isLoggedInAsPartner(): Observable<boolean> {
    return of(this.authenticationService.isLoggedInAsPartner());
  }

  public hasManagerAccess(): Observable<boolean> {
    return this.store.select(fromStore.getHasManagerAccess);
  }

  public hasReportAccess(): Observable<boolean> {
    this.usersService.getUser();
    return this.usersService.allowReports();
  }

  public isValidAppVersion(): Observable<boolean> {
    return of(
      !(
        this.session.isComingFromApp() &&
        this.session.minimumAppVersion.isGreaterThen(
          this.session.currentAppVersion
        )
      )
    );
  }

  public hasAccessToClosingReports(): Observable<boolean> {
    return this.closingReportService.shouldShowClosingReports();
  }

  public showIrsReports(): Observable<boolean> {
    return this.companySettingsServiceHelper.isCompanyIn('us');
  }

  public hasMultipleLocations(): Observable<boolean> {
    return this.establishment
      .getChain()
      .pipe(map((data: Array<CompanyData>) => data.length > 0));
  }

  public getNumberOfLocations(): Observable<number> {
    return this.establishment.getChain().pipe(
      map((data: Array<CompanyData>) => {
        return data.length;
      })
    );
  }

  public hasProductsAccess(): Observable<boolean> {
    this.usersService.getUser();
    return this.usersService.allowProducts();
  }

  public hasKitchenModuleEnabled(): Observable<boolean> {
    return this.modulesService.isModuleEnabled(moduleId.KITCHEN);
  }

  public hasLsAccountingModuleEnabled(): Observable<boolean> {
    return this.modulesService.isModuleEnabled(moduleId.LS_ACCOUNTING);
  }

  public isAllowedToConfigureCFD(): Observable<boolean> {
    return this.cfdService.allowToConfigureCfdSettings$;
  }

  public isBlackboxAccount(): Observable<boolean> {
    return this.companySettingsServiceHelper.isBlackboxAccount();
  }

  public isBlackboxV2Account(): Observable<boolean> {
    return this.companySettingsServiceHelper.isBlackboxV2Account();
  }
}
